import { Badge, cn, Icon, UiLink, type IconName } from '@nexus/ui';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useForMeCount, useShowForMe } from '../for-me/api';
import { isActivePath } from './sidebar';

const tabClasses =
  'flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 text-2xs font-medium text-fg-subtle transition-colors transition-fast active:bg-pressed aria-[current=page]:text-brand-text';

function TabContent({
  icon,
  label,
  count,
  countLabel,
}: {
  icon: IconName;
  label: string;
  /** Number on the icon; `countLabel` reads it out after the label. */
  count?: number;
  countLabel?: string;
}) {
  const counted = count !== undefined && count > 0 ? count : null;
  return (
    <>
      <span className="relative flex">
        <Icon name={icon} className="size-5" />
        {counted !== null && (
          <span aria-hidden className="absolute -top-1.5 left-3 flex">
            <Badge tone="brand" className="h-4 px-1 ring-2 ring-surface">
              {counted > 99 ? '99+' : counted}
            </Badge>
          </span>
        )}
      </span>
      <span className="max-w-full truncate px-1">{label}</span>
      {counted !== null && <span className="sr-only">{countLabel}</span>}
    </>
  );
}

/**
 * Navigation on the phone: fixed entries for the thumb, no modules. Home, "Für mich" with what
 * waits, search, and the menu with the whole navigation (docs/ui/responsive.md).
 */
export function BottomBar({
  menuOpen,
  onOpenMenu,
  onOpenSearch,
}: {
  menuOpen: boolean;
  onOpenMenu: () => void;
  onOpenSearch: () => void;
}) {
  const { t } = useTranslation();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const showForMe = useShowForMe();
  const forMeCount = useForMeCount();
  const home = path === '/';
  const forMe = showForMe && isActivePath(path, '/for-me');

  return (
    <nav
      aria-label={t('shell.bottomBar')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface pb-safe md:hidden"
    >
      <div className="flex h-(--bottombar-h)">
        <UiLink href="/" aria-current={home ? 'page' : undefined} className={tabClasses}>
          <TabContent icon="house" label={t('shell.home')} />
        </UiLink>
        {showForMe && (
          <UiLink href="/for-me" aria-current={forMe ? 'page' : undefined} className={tabClasses}>
            <TabContent
              icon="inbox"
              label={t('shell.forMe')}
              count={forMeCount}
              countLabel={t('shell.forMeCount', { count: forMeCount })}
            />
          </UiLink>
        )}
        <button type="button" onClick={onOpenSearch} className={tabClasses}>
          <TabContent icon="search" label={t('shell.search.open')} />
        </button>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={onOpenMenu}
          className={cn(
            tabClasses,
            // Every other page lives in the menu, so it is marked there.
            !home && !forMe && !isActivePath(path, '/search') && 'text-brand-text',
          )}
        >
          <TabContent icon="menu" label={t('shell.menu')} />
        </button>
      </div>
    </nav>
  );
}
